import { useEffect, useState } from "react";
import { api, apiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Upload, X, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, ExternalLink, Smartphone, Monitor, Image as ImageIcon } from "lucide-react";

const SUB_TABS = [
  { key: "contenuti", label: "Contenuti" },
  { key: "sezioni", label: "Sezioni home" },
  { key: "foto", label: "Foto & media" },
  { key: "contatti", label: "Contatti e social" },
  { key: "seo", label: "SEO" },
  { key: "pagine", label: "Pagine extra" },
  { key: "versioni", label: "Versioni" },
];

const SEO_PAGES = [
  { key: "home", label: "Home" },
  { key: "chi-sono", label: "Chi sono" },
  { key: "servizi", label: "Servizi" },
  { key: "il-metodo", label: "Il metodo" },
  { key: "prenota", label: "Prenota" },
  { key: "contatti", label: "Contatti" },
];

const SECTION_LABELS = {
  hero: "Hero (apertura)",
  intro: "Introduzione",
  services: "I servizi (indice)",
  method: "Il metodo (sezione scura)",
  cta: "Banda di invito",
  custom: "Blocco personalizzato",
};

export default function CmsTab({ draft, setDraft, saveSettings, flash }) {
  const [sub, setSub] = useState("contenuti");
  return (
    <div>
      <nav className="flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="cms-subtabs">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSub(t.key)}
            data-testid={`cms-subtab-${t.key}`}
            className={`shrink-0 rounded-full px-5 py-2 text-[12px] uppercase tracking-widest2 font-medium transition-colors duration-300 ${sub === t.key ? "bg-charcoal text-ivory" : "border border-taupe/40 text-charcoal/70 hover:bg-peach"}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {sub === "contenuti" && <Contenuti draft={draft} setDraft={setDraft} saveSettings={saveSettings} flash={flash} />}
      {sub === "sezioni" && <Sezioni draft={draft} setDraft={setDraft} saveSettings={saveSettings} flash={flash} />}
      {sub === "foto" && <FotoMedia draft={draft} setDraft={setDraft} saveSettings={saveSettings} flash={flash} />}
      {sub === "contatti" && <ContattiTab draft={draft} setDraft={setDraft} saveSettings={saveSettings} />}
      {sub === "seo" && <SeoTab draft={draft} setDraft={setDraft} saveSettings={saveSettings} flash={flash} />}
      {sub === "pagine" && <PagineTab flash={flash} />}
      {sub === "versioni" && <VersioniTab flash={flash} />}
    </div>
  );
}

/* ---------------- Contenuti ---------------- */

function Contenuti({ draft, setDraft, saveSettings, flash }) {
  const c = draft.content || {};
  const set = (group, field, value, sub = null) =>
    setDraft({
      ...draft,
      content: {
        ...c,
        [group]: sub
          ? { ...(c[group] || {}), [sub]: value }
          : { ...(c[group] || {}), [field]: value },
      },
    });

  return (
    <div className="space-y-10" data-testid="cms-contenuti">
      <Group title="Home · Apertura (hero)" description="Il primo schermo del sito: titolo, sottotitolo e i due pulsanti." saveSettings={saveSettings} testId="cms-save-hero">
        <Field label="Sopratitolo" value={c.hero?.overline || ""} onChange={(v) => set("hero", "overline", v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Titolo · prima riga" value={c.hero?.line1 || ""} onChange={(v) => set("hero", "line1", v)} />
          <Field label="Titolo · seconda riga (corsivo)" value={c.hero?.line2 || ""} onChange={(v) => set("hero", "line2", v)} />
        </div>
        <Field label="Sottotitolo" textarea value={c.hero?.subtitle || ""} onChange={(v) => set("hero", "subtitle", v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Pulsante principale · etichetta" value={c.hero?.ctaPrimary || ""} onChange={(v) => set("hero", "ctaPrimary", v)} />
          <Field label="Pulsante principale · destinazione" value={c.hero?.ctaPrimaryLink || "/prenota"} onChange={(v) => set("hero", "ctaPrimaryLink", v)} hint="es. /prenota, /servizi, /pagine/mia-pagina" />
          <Field label="Pulsante secondario · etichetta" value={c.hero?.ctaSecondary || ""} onChange={(v) => set("hero", "ctaSecondary", v)} />
          <Field label="Pulsante secondario · destinazione" value={c.hero?.ctaSecondaryLink || "/chi-sono"} onChange={(v) => set("hero", "ctaSecondaryLink", v)} />
        </div>
      </Group>

      <Group title="Home · Introduzione" saveSettings={saveSettings} testId="cms-save-intro">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Sopratitolo" value={c.intro?.overline || ""} onChange={(v) => set("intro", "overline", v)} />
          <Field label="Servizi in home · titolo" value={c.services?.homeTitle || "Percorsi su misura, in studio e online"} onChange={(v) => set("services", "homeTitle", v)} />
        </div>
        <Field label="Titolo" value={c.intro?.title || ""} onChange={(v) => set("intro", "title", v)} />
        <Field label="Testo" textarea value={c.intro?.text || ""} onChange={(v) => set("intro", "text", v)} />
      </Group>

      <Group title="Il Metodo · dichiarazione e passi" saveSettings={saveSettings} testId="cms-save-metodo">
        <Field label="Dichiarazione (in home la parte dopo i due punti è in corsivo)" textarea value={c.method?.statement || ""} onChange={(v) => set("method", "statement", v)} />
        {(c.method?.steps || []).map((s, i) => (
          <div key={i} className="border border-taupe/25 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={`Passo ${s.n} · titolo`} value={s.title || ""} onChange={(v) => set("method", "steps", c.method.steps.map((x, j) => (j === i ? { ...x, title: v } : x)), "steps")} />
            <div className="sm:col-span-2">
              <Field label="Testo" textarea rows={2} value={s.text || ""} onChange={(v) => set("method", "steps", c.method.steps.map((x, j) => (j === i ? { ...x, text: v } : x)), "steps")} />
            </div>
          </div>
        ))}
      </Group>

      <Group title="Chi sono" description="I testi della pagina Chi sono: i campi tra parentesi quadre sono segnaposto da completare." saveSettings={saveSettings} testId="cms-save-about">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Titolo" value={c.about?.title || ""} onChange={(v) => set("about", "title", v)} />
          <Field label="Sottotitolo di apertura (lead)" value={c.about?.lead || ""} onChange={(v) => set("about", "lead", v)} />
        </div>
        <Field
          label="Biografia completa"
          textarea
          rows={9}
          value={c.about?.biography || ""}
          onChange={(v) => set("about", "biography", v)}
          hint="Puoi scrivere più paragrafi: premi Invio per andare a capo. Appena togli il segnaposto, la targhetta 'Contenuto da completare' sparisce dal sito."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Sovratitolo della pagina" value={c.about?.aboutEyebrow || "Chi sono"} onChange={(v) => set("about", "aboutEyebrow", v)} />
          <Field label="Titolo della sezione biografia" value={c.about?.biographyTitle || "Biografia"} onChange={(v) => set("about", "biographyTitle", v)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Field label="Titolo card · formazione" value={c.about?.educationTitle || "Formazione universitaria"} onChange={(v) => set("about", "educationTitle", v)} />
          <Field label="Titolo card · aggiornamento" value={c.about?.trainingTitle || "Aggiornamento professionale"} onChange={(v) => set("about", "trainingTitle", v)} />
          <Field label="Titolo card · ordine" value={c.about?.registrationTitle || "Iscrizione all'ordine professionale"} onChange={(v) => set("about", "registrationTitle", v)} />
        </div>
        <div className="grid grid-cols-1 gap-5">
          <Field label="Formazione" textarea value={c.about?.education || ""} onChange={(v) => set("about", "education", v)} />
          <Field label="Aggiornamento professionale" textarea value={c.about?.training || ""} onChange={(v) => set("about", "training", v)} />
          <Field label="Iscrizione all'ordine" textarea value={c.about?.registration || ""} onChange={(v) => set("about", "registration", v)} />
        </div>
        <Field label="Filosofia" textarea value={c.about?.philosophyText || ""} onChange={(v) => set("about", "philosophyText", v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Titolo della filosofia" value={c.about?.philosophyHeading || "Equilibrio, non regole rigide."} onChange={(v) => set("about", "philosophyHeading", v)} />
          <Field
            label="Citazione (se vuota usa la dichiarazione del Metodo)"
            value={c.about?.biographyQuote || ""}
            onChange={(v) => set("about", "biographyQuote", v)}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Banda finale · titolo" value={c.about?.ctaTitle || "Iniziamo insieme."} onChange={(v) => set("about", "ctaTitle", v)} />
          <Field label="Banda finale · pulsante" value={c.about?.ctaButton || "Prenota una consulenza"} onChange={(v) => set("about", "ctaButton", v)} />
        </div>
      </Group>

      <Group title="Pagine Prenota, Contatti e banda di invito" saveSettings={saveSettings} testId="cms-save-pagine">
        <div className="grid grid-cols-1 gap-5">
          <Field label="Prenota · testo di apertura" textarea value={c.prenota?.text || ""} onChange={(v) => set("prenota", "text", v)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Contatti · titolo" value={c.contacts?.title || ""} onChange={(v) => set("contacts", "title", v)} />
            <Field label="Contatti · testo" value={c.contacts?.text || ""} onChange={(v) => set("contacts", "text", v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Banda di invito · titolo (ultima parola in corsivo)" value={c.cta?.title || ""} onChange={(v) => set("cta", "title", v)} />
            <Field label="Banda di invito · testo" textarea value={c.cta?.text || ""} onChange={(v) => set("cta", "text", v)} />
            <Field label="Banda di invito · pulsante" value={c.cta?.button || ""} onChange={(v) => set("cta", "button", v)} />
            <Field label="Banda di invito · destinazione pulsante" value={c.cta?.buttonLink || "/prenota"} onChange={(v) => set("cta", "buttonLink", v)} />
          </div>
        </div>
      </Group>
    </div>
  );
}

/* ---------------- Sezioni home ---------------- */

function Sezioni({ draft, setDraft, saveSettings, flash }) {
  const sections = draft.sections || [];
  const setSections = (next) => setDraft({ ...draft, sections: next });
  const move = (i, dir) => {
    const next = [...sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
    saveSettings();
  };
  const addCustom = () => {
    const id = `custom-${Math.random().toString(36).slice(2, 7)}`;
    setSections([...sections, { id, type: "custom", visible: true, title: "Un nuovo blocco", text: "Scrivi qui il testo del blocco.", image: "", ctaLabel: "", ctaUrl: "" }]);
    flash("Blocco aggiunto: compila i campi e salva");
  };

  return (
    <div className="space-y-5" data-testid="cms-sezioni">
      <SectionHead
        title="Sezioni della homepage"
        description="Mostra, nascondi e riordina le sezioni della home. Le sezioni personalizzate sono blocchi con titolo, testo, foto e pulsante."
      />
      {sections.map((s, i) => (
        <div key={s.id} className="border border-taupe/25 rounded-2xl p-5" data-testid={`cms-section-${s.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-display text-xl text-deeptaupe">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-medium text-sm">{SECTION_LABELS[s.type] || s.type}</span>
            <span className="text-[11px] uppercase tracking-widest2 text-charcoal/40">{s.id}</span>
            <span className="ml-auto flex items-center gap-1">
              <button onClick={() => move(i, -1)} aria-label="Sposta su" data-testid={`cms-section-up-${s.id}`} className="p-2 rounded-full hover:bg-peach disabled:opacity-25" disabled={i === 0}><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} aria-label="Sposta giù" data-testid={`cms-section-down-${s.id}`} className="p-2 rounded-full hover:bg-peach disabled:opacity-25" disabled={i === sections.length - 1}><ArrowDown size={15} /></button>
              <button
                onClick={() => { setSections(sections.map((x) => (x.id === s.id ? { ...x, visible: !x.visible } : x))); saveSettings(); }}
                aria-label={s.visible ? "Nascondi sezione" : "Mostra sezione"}
                data-testid={`cms-section-visible-${s.id}`}
                className={`p-2 rounded-full hover:bg-peach ${s.visible ? "text-deeptaupe" : "text-charcoal/35"}`}
              >
                {s.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              {s.type === "custom" && (
                <button onClick={() => { setSections(sections.filter((x) => x.id !== s.id)); saveSettings(); }} aria-label="Elimina blocco" data-testid={`cms-section-delete-${s.id}`} className="p-2 rounded-full text-red-500/70 hover:text-red-700"><Trash2 size={15} /></button>
              )}
            </span>
          </div>
          {s.type === "custom" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="Titolo" value={s.title || ""} onChange={(v) => setSections(sections.map((x) => (x.id === s.id ? { ...x, title: v } : x)))} />
              <Field label="Etichetta pulsante (opzionale)" value={s.ctaLabel || ""} onChange={(v) => setSections(sections.map((x) => (x.id === s.id ? { ...x, ctaLabel: v } : x)))} />
              <Field label="Destinazione pulsante" value={s.ctaUrl || ""} onChange={(v) => setSections(sections.map((x) => (x.id === s.id ? { ...x, ctaUrl: v } : x)))} />
              <div className="sm:col-span-2">
                <Field label="Testo" textarea value={s.text || ""} onChange={(v) => setSections(sections.map((x) => (x.id === s.id ? { ...x, text: v } : x)))} />
              </div>
              <div className="sm:col-span-2">
                <Field label="Percorso immagine (opzionale)" value={s.image || ""} onChange={(v) => setSections(sections.map((x) => (x.id === s.id ? { ...x, image: v } : x)))} hint="caricala dalla scheda Foto & media e incolla qui l'indirizzo" />
              </div>
            </div>
          )}
        </div>
      ))}
      <button onClick={addCustom} data-testid="cms-section-add" className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach">
        <Plus size={14} /> Aggiungi blocco personalizzato
      </button>
      <div><SaveBtn onClick={saveSettings} testId="cms-save-sezioni" /></div>
    </div>
  );
}

/* ---------------- Foto & media ---------------- */

const PHOTO_LABELS = {
  hero: "Home hero — supermercato",
  about: "Chi sono — fascia copertina con carota",
  philosophy: "Chi sono · filosofia — supermercato con insalata",
  services: "Servizi — schiena con due broccoli",
  aboutSecond: "Chi sono — seconda foto (primo piano)",
  profile: "Home intro — ritratto di profilo",
  method: "Il metodo — broccoli bilanciere",
  lifestyle: "Home intro — natura morta prodotti",
};

function FotoMedia({ draft, setDraft, saveSettings, flash }) {
  const [library, setLibrary] = useState([]);
  const [pickKey, setPickKey] = useState(null);

  const loadLibrary = () => api.get("/admin/media").then(({ data }) => setLibrary(data.media || [])).catch((e) => flash(apiErrorDetail(e), false));
  useEffect(() => {
    loadLibrary();
  }, []);

  const upload = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      flash("Immagine caricata nella libreria");
      loadLibrary();
      return data.url;
    } catch (e) {
      flash(apiErrorDetail(e), false);
      return null;
    }
  };

  const photos = draft.photos || {};
  const framing = draft.photosFraming || {};
  const setPhoto = (key, url) => setDraft({ ...draft, photos: { ...photos, [key]: url } });
  const setFraming = (key, x, y) => setDraft({ ...draft, photosFraming: { ...framing, [key]: { x: Number(x), y: Number(y) } } });

  return (
    <div className="space-y-8" data-testid="cms-foto">
      <SectionHead
        title="Foto delle sezioni e libreria"
        description="Sostituisci le foto, scegline una dalla libreria o carica nuove immagini (jpg, png o webp, max 8 MB). Il ritaglio imposta il punto della foto che resta ben inquadrato."
      />
      {Object.keys(PHOTO_LABELS).map((key) => (
        <div key={key} className="border border-taupe/25 rounded-2xl p-5 space-y-4" data-testid={`cms-photo-${key}`}>
          <div className="flex flex-col sm:flex-row gap-5">
            <img src={photos[key]} alt={PHOTO_LABELS[key]} className="w-full sm:w-40 h-28 object-cover rounded-xl border border-taupe/20" style={{ objectPosition: framing[key] ? `${framing[key].x}% ${framing[key].y}%` : undefined }} />
            <div className="flex-1 space-y-3">
              <p className="text-sm font-medium">{PHOTO_LABELS[key]}</p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 text-deeptaupe cursor-pointer hover:text-charcoal" data-testid={`cms-photo-upload-${key}`}>
                  <Upload size={14} /> Carica
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    const url = await upload(file);
                    if (url) { setPhoto(key, url); flash("Foto aggiornata: ricorda di salvare"); }
                  }} />
                </label>
                <button onClick={() => setPickKey(pickKey === key ? null : key)} data-testid={`cms-photo-library-${key}`} className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 text-charcoal/60 hover:text-deeptaupe">
                  <ImageIcon size={14} /> Scegli dalla libreria
                </button>
              </div>
              <input
                type="text"
                value={photos[key] || ""}
                onChange={(e) => setPhoto(key, e.target.value)}
                data-testid={`cms-photo-url-${key}`}
                className="w-full rounded-xl border border-taupe/40 bg-transparent px-4 py-2.5 text-xs"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
            <div className="space-y-2">
              <p className="text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe">Ritaglio · punto focale</p>
              <div className="flex items-center gap-4">
                <span className="text-[12px] text-charcoal/60 w-10">Orizz. {framing[key]?.x ?? 50}%</span>
                <input type="range" min="0" max="100" value={framing[key]?.x ?? 50} onChange={(e) => setFraming(key, e.target.value, framing[key]?.y ?? 50)} data-testid={`cms-photo-cropx-${key}`} className="flex-1 accent-[#6B5C50]" />
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[12px] text-charcoal/60 w-10">Vert. {framing[key]?.y ?? 50}%</span>
                <input type="range" min="0" max="100" value={framing[key]?.y ?? 50} onChange={(e) => setFraming(key, framing[key]?.x ?? 50, e.target.value)} data-testid={`cms-photo-cropy-${key}`} className="flex-1 accent-[#6B5C50]" />
              </div>
            </div>
            <div className="w-full sm:w-44 aspect-video rounded-xl overflow-hidden border border-taupe/20" data-testid={`cms-photo-preview-${key}`}>
              <img src={photos[key]} alt="" className="w-full h-full object-cover" style={{ objectPosition: framing[key] ? `${framing[key].x}% ${framing[key].y}%` : "50% 50%" }} />
            </div>
          </div>
          {pickKey === key && (
            <div className="border-t border-taupe/20 pt-4" data-testid={`cms-library-picker-${key}`}>
              <p className="text-[12px] uppercase tracking-widest2 text-charcoal/50 mb-3">Immagini nella libreria</p>
              {library.length === 0 && <p className="text-sm text-charcoal/50 mb-3">La libreria è vuota: carica una foto sopra.</p>}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {library.map((m) => (
                  <button key={m.mid} onClick={() => { setPhoto(key, `/api/media/${m.mid}`); setPickKey(null); flash("Foto scelta: ricorda di salvare"); }} className="relative group" data-testid={`cms-library-pick-${m.mid}`}>
                    <img src={`/api/media/${m.mid}`} alt={m.filename} className="w-full aspect-square object-cover rounded-lg border border-taupe/20" />
                    <span onClick={async (e) => { e.stopPropagation(); if (window.confirm("Eliminare questa immagine dalla libreria?")) { try { await api.delete(`/admin/media/${m.mid}`); loadLibrary(); } catch (err) { flash(apiErrorDetail(err), false); } } }} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`cms-library-delete-${m.mid}`}>
                      <X size={12} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      <Group title="Logo e risorse del brand" description="Il logo compare nella barra di navigazione, nel footer e nel pannello." saveSettings={saveSettings} testId="cms-save-brand">
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <img src={(draft.brand || {}).logo || "/images/logo-transparent.png"} alt="Logo K Nutrition" className="h-14" data-testid="cms-brand-logo-preview" />
          <div className="flex-1 w-full space-y-3">
            <input
              type="text"
              value={(draft.brand || {}).logo || ""}
              onChange={(e) => setDraft({ ...draft, brand: { ...(draft.brand || {}), logo: e.target.value } })}
              data-testid="cms-brand-logo-url"
              className="w-full rounded-xl border border-taupe/40 bg-transparent px-4 py-2.5 text-xs"
            />
            <label className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 text-deeptaupe cursor-pointer hover:text-charcoal" data-testid="cms-brand-logo-upload">
              <Upload size={14} /> Carica nuovo logo
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const url = await upload(file);
                if (url) { setDraft({ ...draft, brand: { ...(draft.brand || {}), logo: url } }); flash("Logo aggiornato: ricorda di salvare"); }
              }} />
            </label>
          </div>
        </div>
      </Group>

      <div><SaveBtn onClick={saveSettings} testId="cms-save-foto" /></div>
    </div>
  );
}

/* ---------------- SEO ---------------- */

function SeoTab({ draft, setDraft, saveSettings, flash }) {
  const seo = draft.seo || {};
  const setSeo = (page, field, value) => setDraft({ ...draft, seo: { ...seo, [page]: { ...(seo[page] || {}), [field]: value } } });
  return (
    <div className="space-y-6" data-testid="cms-seo">
      <SectionHead
        title="Titoli e descrizioni per Google"
        description="Sono il titolo e la descrizione che compaiono nei risultati di ricerca e quando il sito viene condiviso su WhatsApp o social."
      />
      {SEO_PAGES.map((p) => (
        <div key={p.key} className="border border-taupe/25 rounded-2xl p-5 space-y-4" data-testid={`cms-seo-${p.key}`}>
          <p className="text-sm font-medium">{p.label}</p>
          <Field label={`Titolo (${(seo[p.key]?.title || "").length}/150)`} value={seo[p.key]?.title || ""} onChange={(v) => setSeo(p.key, "title", v)} />
          <Field label={`Descrizione (${(seo[p.key]?.description || "").length}/320)`} textarea value={seo[p.key]?.description || ""} onChange={(v) => setSeo(p.key, "description", v)} />
        </div>
      ))}
      <SaveBtn onClick={saveSettings} testId="cms-save-seo" />
    </div>
  );
}

/* ---------------- Pagine extra ---------------- */

function PagineTab({ flash }) {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/pages").then(({ data }) => setPages(data.pages || [])).catch((e) => flash(apiErrorDetail(e), false));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (form.pid) {
        await api.put(`/admin/pages/${form.pid}`, form);
        flash("Pagina aggiornata: ora è pubblica sul sito");
      } else {
        await api.post("/admin/pages", form);
        flash("Pagina creata: è pubblica sul sito");
      }
      setForm(null);
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-pagine">
      <SectionHead
        title="Pagine informative aggiuntive"
        description="Crea pagine libere (per esempio 'Preparazione alla visita' o 'Regalo buono'): compaiono automaticamente nel footer del sito."
      />
      <button
        onClick={() => setForm({ pid: null, slug: "", title: "", content: "", visible: true, order: pages.length + 1 })}
        data-testid="cms-page-new"
        className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach"
      >
        <Plus size={14} /> Nuova pagina
      </button>

      {form && (
        <div className="bg-white rounded-[24px] shadow-soft p-6 space-y-4" data-testid="cms-page-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Titolo" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <Field label="Indirizzo (slug)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} hint="es. preparazione-visita" />
            <Field label="Ordine nel footer" value={form.order} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
          </div>
          <Field label="Contenuto" textarea rows={8} value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="w-4 h-4 accent-[#6B5C50]" data-testid="cms-page-visible" />
            Visibile sul sito
          </label>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy || !form.title || !form.slug} data-testid="cms-page-save" className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors disabled:opacity-40">Salva pagina</button>
            <button onClick={() => setForm(null)} data-testid="cms-page-cancel" className="rounded-full border border-taupe/40 text-charcoal/60 text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-peach">Annulla</button>
            {form.pid && (
              <a href={`/pagine/${form.slug}`} target="_blank" rel="noopener noreferrer" data-testid="cms-page-view" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 text-deeptaupe hover:underline">
                <ExternalLink size={13} /> Vedi
              </a>
            )}
          </div>
        </div>
      )}

      {pages.length === 0 && <p className="text-sm text-charcoal/50">Nessuna pagina extra: creane una con "Nuova pagina".</p>}
      <div className="space-y-3">
        {pages.map((p) => (
          <div key={p.pid} className="flex flex-wrap items-center gap-3 bg-white rounded-2xl px-5 py-4" data-testid={`cms-page-row-${p.slug}`}>
            <span className="font-medium text-sm">{p.title}</span>
            <span className="text-[12px] text-charcoal/50">/pagine/{p.slug}</span>
            {!p.visible && <span className="text-[11px] uppercase tracking-widest2 text-charcoal/40">Nascosta</span>}
            <span className="ml-auto flex gap-3">
              <button onClick={() => setForm({ ...p })} data-testid={`cms-page-edit-${p.slug}`} className="text-[12px] uppercase tracking-widest2 text-charcoal/60 hover:text-deeptaupe">Modifica</button>
              <button onClick={async () => { if (window.confirm(`Eliminare la pagina "${p.title}"?`)) { try { await api.delete(`/admin/pages/${p.pid}`); load(); flash("Pagina eliminata"); } catch (e) { flash(apiErrorDetail(e), false); } } }} data-testid={`cms-page-delete-${p.slug}`} aria-label="Elimina pagina" className="text-red-500/70 hover:text-red-700"><Trash2 size={15} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Contatti e social ---------------- */

function ContattiTab({ draft, setDraft, saveSettings }) {
  const ct = draft.contact || {};
  const setC = (field, value) => setDraft({ ...draft, contact: { ...ct, [field]: value } });
  return (
    <div className="space-y-6" data-testid="cms-contatti">
      <SectionHead
        title="Contatti, social e indirizzo"
        description="Email, numeri di telefono e WhatsApp, profilo Instagram e indirizzo dello studio: aggiornano tutto il sito."
      />
      <div className="border border-taupe/25 rounded-[24px] p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Email" value={ct.email || ""} onChange={(v) => setC("email", v)} />
          <Field label="Telefono (visualizzato)" value={ct.phone || ""} onChange={(v) => setC("phone", v)} />
          <Field label="Telefono (link di chiamata)" value={ct.phoneHref || ""} onChange={(v) => setC("phoneHref", v)} />
          <Field label="Numero WhatsApp (solo cifre, con prefisso)" value={ct.whatsapp || ""} onChange={(v) => setC("whatsapp", v)} />
          <Field label="URL Instagram" value={ct.instagram || ""} onChange={(v) => setC("instagram", v)} />
          <Field label="Nome Instagram (@...)" value={ct.instagramHandle || ""} onChange={(v) => setC("instagramHandle", v)} />
          <Field label="Indirizzo (via)" value={ct.addressStreet || ""} onChange={(v) => setC("addressStreet", v)} />
          <Field label="Città" value={ct.addressCity || ""} onChange={(v) => setC("addressCity", v)} />
          <Field label="Paese" value={ct.addressCountry || ""} onChange={(v) => setC("addressCountry", v)} />
        </div>
        <Field label="Ricerca Google Maps" value={ct.mapsQuery || ""} onChange={(v) => setC("mapsQuery", v)} />
        <SaveBtn onClick={saveSettings} testId="cms-save-contatti" />
      </div>
    </div>
  );
}

/* ---------------- Versioni ---------------- */

function VersioniTab({ flash }) {
  const [versions, setVersions] = useState([]);
  const load = () => api.get("/admin/versions").then(({ data }) => setVersions(data.versions || [])).catch((e) => flash(apiErrorDetail(e), false));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6" data-testid="cms-versioni">
      <SectionHead
        title="Versioni pubblicate"
        description="Ogni pubblicazione salva una versione del sito. Ripristina una versione per riporterla in bozza: controlla l'anteprima e premi Pubblica per applicarla."
      />
      {versions.length === 0 && <p className="text-sm text-charcoal/50">Nessuna versione salvata: appara dopo la prima pubblicazione.</p>}
      <div className="space-y-3">
        {versions.map((v) => (
          <div key={v.version} className="flex flex-wrap items-center gap-4 bg-white rounded-2xl px-5 py-4" data-testid={`cms-version-row-${v.version}`}>
            <span className="font-display text-xl text-deeptaupe">v{v.version}</span>
            <span className="text-sm text-charcoal/70">
              {new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(v.published_at))}
            </span>
            <button
              onClick={async () => {
                try {
                  await api.post(`/admin/versions/${v.version}/restore`);
                  flash(`Versione v${v.version} riportata in bozza: controllala e premi Pubblica`);
                } catch (e) {
                  flash(apiErrorDetail(e), false);
                }
              }}
              data-testid={`cms-version-restore-${v.version}`}
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-2 hover:bg-peach"
            >
              <RotateCcw size={13} /> Ripristina in bozza
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- shared pieces ---------------- */

function SectionHead({ title, description }) {
  return (
    <div className="mb-2">
      <h2 className="font-display text-2xl lg:text-3xl">{title}</h2>
      {description && <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">{description}</p>}
    </div>
  );
}

function SaveBtn({ onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="rounded-full bg-deeptaupe text-ivory text-[13px] uppercase tracking-widest2 font-medium px-10 py-4 transition-colors duration-300 hover:bg-charcoal"
    >
      Salva bozza
    </button>
  );
}

function Group({ title, description, children, saveSettings, testId }) {
  return (
    <div className="border border-taupe/25 rounded-[24px] p-6 space-y-5" data-testid={testId}>
      <SectionHead title={title} description={description} />
      {children}
      <SaveBtn onClick={saveSettings} testId={`${testId}-save`} />
    </div>
  );
}

function Field({ label, value, onChange, textarea = false, hint = "", rows = 4 }) {
  const cls = "w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm focus:border-deeptaupe focus:outline-none";
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">{label}</span>
      {textarea ? (
        <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
      {hint && <span className="block text-[11px] text-charcoal/45 mt-1">{hint}</span>}
    </label>
  );
}
